import React, { createContext, useContext, useMemo } from "react";
import { useAuth } from "./AuthContext";
import { useUnreadChats } from "./UnreadChatsContext";
import { NotificationsFeedContext, useNotificationsSnapshot } from "../hooks/useNotificationsSnapshot";
import { isBellCountable } from "../lib/notificationMeta";

/**
 * Unread counters for the shell (bell dot, Notifications and Chats nav badges).
 *
 *   useUnread() -> {
 *     notifications          bell count: unread docs that pass isBellCountable (no chat-message notices)
 *     notificationsVersion   bumps on every notifications snapshot (pages refetch on change)
 *     unreadNotifs           every unread notification doc (newest first), incl. chat-message ones
 *     chats                  Chats badge, in mobile's units (UnreadChatsContext badgeCount; 0 until C1 lands)
 *     unreadError            true while the shared listener is failing (it re-opens with backoff):
 *                            the counts above are stale, so badges show "unknown", never "0"
 *   }
 *
 * UnreadProvider owns the ONE unread-notifications listener (hooks/useNotificationsSnapshot.js)
 * and shares it through NotificationsFeedContext, which UnreadChatsProvider (mounted inside this
 * provider) consumes. `chats` is merged in useUnread() because the chats provider sits below this one.
 * The provider sits inside the uid-keyed AccountScope, so an account switch tears the listener down.
 */
const UnreadContext = createContext({ notifications: 0, notificationsVersion: 0, unreadNotifs: [], unreadError: false });

export function useUnread() {
  const base = useContext(UnreadContext);
  const chats = useUnreadChats()?.badgeCount ?? 0;
  return useMemo(() => ({ ...base, chats }), [base, chats]);
}

export function UnreadProvider({ children }) {
  const { user, emailVerified } = useAuth();
  const uid = user && emailVerified ? user.uid : null;
  const feed = useNotificationsSnapshot(uid);

  const value = useMemo(
    () => ({
      notifications: feed.docs.filter(isBellCountable).length,
      notificationsVersion: feed.version,
      unreadNotifs: feed.docs,
      unreadError: !!feed.error,
    }),
    [feed.docs, feed.version, feed.error]
  );

  return (
    <NotificationsFeedContext.Provider value={feed}>
      <UnreadContext.Provider value={value}>{children}</UnreadContext.Provider>
    </NotificationsFeedContext.Provider>
  );
}
